import {
  Lock, TrendingUp, Wrench, ShieldAlert, Eye,
  Layers, Bath, DoorOpen, ChefHat, Paintbrush, Home, Leaf,
} from "lucide-react";

const STRATEGY_ICONS = {
  category_shift_bedroom: DoorOpen,
  internal_reconfiguration: Layers,
  kitchen_reconfiguration: ChefHat,
  bathroom_additional: Bath,
  bathroom_ensuite: Bath,
  secondary_space_separation: DoorOpen,
  cosmetic_perception: Paintbrush,
  curb_appeal: Home,
  energy_future_proofing: Leaf,
};

const fmt = (v) => (v >= 1000 ? `£${Math.round(v / 1000)}k` : `£${v}`);

function roiBadge(s) {
  if (!s.range || !s.implementation_range) return null;
  const net = (s.range.lower + s.range.upper) / 2 - (s.implementation_range.lower + s.implementation_range.upper) / 2;
  const cost = (s.implementation_range.lower + s.implementation_range.upper) / 2;
  if (cost === 0) return { label: "Strong", cls: "text-cipher-green bg-cipher-green/15" };
  const r = net / cost;
  if (r > 1) return { label: "Strong", cls: "text-cipher-green bg-cipher-green/15" };
  if (r > 0.3) return { label: "Moderate", cls: "text-cipher-gold bg-cipher-gold/15" };
  return { label: "Weak", cls: "text-amber-400 bg-amber-400/15" };
}

export function WhatsItWorth({ valueHeadroom, isUnlocked, ceilingCaution }) {
  if (!valueHeadroom?.range) return null;

  const { range, strategies, supportive_strategies, value_traps } = valueHeadroom;
  const allStrategies = [...(strategies || []), ...(supportive_strategies || [])];
  const trapCount = value_traps?.length || 0;

  return (
    <div data-testid="section-whats-it-worth" className="space-y-3">
      {/* Value headline */}
      <div className="relative bg-white/[0.04] rounded-xl p-4 text-center overflow-hidden">
        {!isUnlocked && (
          <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[5px] bg-black/30 rounded-xl">
            <div className="flex items-center gap-2 bg-black/60 px-4 py-2 rounded-full border border-white/10">
              <Eye className="w-3.5 h-3.5 text-cipher-gold" />
              <span className="text-xs text-white/60">Unlock to reveal</span>
            </div>
          </div>
        )}
        <div className={!isUnlocked ? "blur-md select-none" : ""}>
          <p className="text-2xl font-bold text-white tracking-tight">
            {fmt(range.lower)} – {fmt(range.upper)}
          </p>
          <p className="text-xs text-white/40 mt-1">estimated value uplift from similar improvements nearby</p>
        </div>
      </div>

      {/* Strategy list — compact */}
      {allStrategies.map((s) => {
        const Icon = STRATEGY_ICONS[s.id] || Layers;
        const badge = roiBadge(s);
        return (
          <div
            key={s.id}
            className="flex items-center gap-3 bg-white/[0.04] rounded-lg px-3 py-2.5"
            data-testid={`value-card-${s.id}`}
          >
            <Icon className="w-4 h-4 text-cipher-green/70 flex-shrink-0" />
            <span className="text-sm text-white/80 flex-1 truncate">{s.label}</span>
            {s.implementation_range && (
              <span className="text-[11px] text-white/35 flex-shrink-0 hidden sm:inline">
                {fmt(s.implementation_range.lower)}–{fmt(s.implementation_range.upper)}
              </span>
            )}
            {badge && (
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 ${badge.cls}`}
                data-testid={`roi-badge-${s.id}`}
              >
                {badge.label}
              </span>
            )}
            {!isUnlocked && <Lock className="w-3 h-3 text-white/20 flex-shrink-0" />}
          </div>
        );
      })}

      {/* Value traps teaser */}
      {trapCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/[0.06] border border-amber-500/15 rounded-lg">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-400/60 flex-shrink-0" />
          {isUnlocked ? (
            <div className="space-y-1">
              {value_traps.map((t) => (
                <p key={t.id} className="text-xs text-amber-300/70">{t.label}</p>
              ))}
            </div>
          ) : (
            <span className="text-xs text-amber-300/60">
              {trapCount} risk{trapCount > 1 ? "s" : ""} flagged —{" "}
              <span className="text-amber-400/80 font-medium">unlock to see</span>
            </span>
          )}
        </div>
      )}

      {ceilingCaution && (
        <p className="text-[11px] text-amber-400/60 text-center">
          A price ceiling exists in this area — returns may be limited.
        </p>
      )}
    </div>
  );
}
